import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Stack,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  IconButton,
  Card,
  alpha,
} from '@mui/material';
// import TCPACheckbox from './TCPACheckbox';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import {
  BACKGROUND_GRADIENT,
  BUTTON_BG_COLOR,
} from './PropertySpotlightDialog';
import { validatePhone } from '../../utils/validation';
import { NewLeadQuickOption } from './PropertyDetailsDialog';

interface OnboardingFormProps {
  onSubmit: (data: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    tcpaConsent: boolean;
  }) => Promise<void>;
  onBack: () => void;
  newLeadOption: NewLeadQuickOption;
  isLoading: boolean;
  title?: string;
  description?: string;
  submitButtonText?: string;
  footerText?: string;
  imagePath?: string;
}

const OnboardingForm: React.FC<OnboardingFormProps> = ({
  onSubmit,
  isLoading,
  title,
  description,
  submitButtonText,
  footerText,
  imagePath,
  onBack,
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    tcpaConsent: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First Name is required.';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last Name is required.';
    }
    if (!formData.email.trim()) {
      // newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email must be a valid email address.';
    }

    const validation = validatePhone(formData.phone);
    if (!validation.valid) {
      newErrors.phone = validation.error || '';
    }

    // if (!formData.phone.trim()) {
    //   newErrors.phone = 'Mobile number is required';
    // }

    if (!formData.tcpaConsent) {
      newErrors.tcpaConsent =
        'errors.you_must_agree_to_receive_messages_to_continue';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validate()) {
      await onSubmit(formData);
    }
  };

  return (
    <Box
      sx={{
        pb: 3,
        overflowY: 'auto',
        borderRadius: { xs: 2, sm: 3 },
        flex: 1,
        background: BACKGROUND_GRADIENT,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
          borderRadius: '8px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 3,
          py: 1,
          mb: 2,
          textAlign: 'center',
          background:
            'linear-gradient(to right, #B1CAFF, #ffffff, #ffffff, #ffffff)',
          borderBottom: '1px solid',
          borderColor: (theme) => alpha(theme.palette.primary.light, 0.1),
        }}
      >
        <IconButton
          onClick={onBack}
          disabled={isLoading}
          sx={{ mr: 0.5, p: 0.5, pl: 0 }}
          aria-label='Go back'
        >
          <KeyboardBackspaceIcon sx={{ color: 'primary.light' }} />
        </IconButton>
        <Typography fontWeight={400} color={'primary.light'}>
          Back to Property
        </Typography>
      </Box>

      <Box sx={{ px: 2 }}>
        <Typography
          variant='h6'
          fontWeight={500}
          sx={{ mb: 1, textAlign: 'center' }}
        >
          {title || ''}
        </Typography>

        <Typography variant='body2' sx={{ mb: 2, textAlign: 'center' }}>
          {description || ''}
        </Typography>

        {imagePath && (
          <Box m={0} textAlign={'center'}>
            <Box
              component={'img'}
              src={imagePath}
              sx={{ objectFit: 'contain', maxWidth: '50%', mx: 'auto', my: 1 }}
            />
          </Box>
        )}

        <Card
          sx={{
            p: 2,
            backgroundColor: '#fff',
            // backgroundImage: BACKGROUND_GRADIENT_TRANSPARENT,
          }}
        >
          <TextField
            fullWidth
            label={'First Name'}
            value={formData.firstName}
            onChange={(e) =>
              setFormData({ ...formData, firstName: e.target.value })
            }
            error={!!errors.firstName}
            helperText={errors.firstName}
            disabled={isLoading}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label={'Last Name'}
            value={formData.lastName}
            onChange={(e) =>
              setFormData({ ...formData, lastName: e.target.value })
            }
            error={!!errors.lastName}
            helperText={errors.lastName}
            disabled={isLoading}
            sx={{ mb: 2 }}
          />

          {/* <TextField
        fullWidth
        type='email'
        label='Email (optional)'
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        error={!!errors.email}
        helperText={errors.email}
        disabled={isLoading}
        sx={{ mb: 2 }}
      /> */}

          <TextField
            fullWidth
            type='tel'
            label={'Mobile Phone Number'}
            value={formData.phone}
            onChange={(e) =>
              setFormData({ ...formData, phone: e.target.value })
            }
            error={!!errors.phone}
            helperText={errors.phone}
            disabled={isLoading}
            sx={{ mb: 3 }}
          />

          {/* <TCPACheckbox
        checked={formData.tcpaConsent}
        onChange={(checked) =>
          setFormData({ ...formData, tcpaConsent: checked })
        }
        error={!!errors.tcpaConsent}
        disabled={isLoading}
      /> */}

          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            sx={{ mb: 1.5 }}
          >
            {/* Left side: Terms checkbox */}
            <FormControl
              error={!!errors.tcpaConsent}
              component='fieldset'
              variant='standard'
            >
              <FormControlLabel
                control={
                  <Checkbox
                    name='terms'
                    checked={formData.tcpaConsent}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        tcpaConsent: e.target.checked,
                      })
                    }
                    color='primary'
                    //color='success'
                    sx={{
                      '& .MuiSvgIcon-root': {
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                      },
                      mt: 0,
                    }}
                  />
                }
                label={
                  <span style={{ marginTop: '5px' }}>
                    <Typography
                      component='span'
                      sx={{
                        fontSize: '14px',
                        userSelect: 'none',
                      }}
                    >
                      I agree to the{' '}
                    </Typography>
                    <Typography
                      component='span'
                      variant='body2'
                      fontWeight={500}
                      color='primary.light'
                      onClick={(e) => {
                        e.preventDefault();
                        window.open('/terms', '_blank');
                      }}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          color: 'primary.main',
                        },
                      }}
                    >
                      Terms & Conditions
                    </Typography>
                    <Typography
                      component='span'
                      sx={{
                        fontSize: '14px',
                        userSelect: 'none',
                      }}
                    >
                      {' '}
                      and{' '}
                    </Typography>
                    <Typography
                      component='span'
                      variant='body2'
                      fontWeight={500}
                      color='primary.light'
                      onClick={(e) => {
                        e.preventDefault();
                        window.open('/policy', '_blank');
                      }}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          color: '#1890FF',
                        },
                      }}
                    >
                      Privacy Policy
                    </Typography>{' '}
                    <Typography
                      component='span'
                      sx={{
                        fontSize: '14px',
                        userSelect: 'none',
                      }}
                    >
                      KnoCard LLC DBA QR Buddy
                    </Typography>
                  </span>
                }
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': {
                    display: 'inline-flex',
                    alignItems: 'flex-start', // 👈 keep text aligned at top too
                    lineHeight: 1.4,
                  },
                }}
              />
              {!!errors.tcpaConsent && (
                <FormHelperText>
                  You must accept the terms and conditions.
                </FormHelperText>
              )}
            </FormControl>
          </Stack>

          <Button
            fullWidth
            variant='contained'
            onClick={handleSubmit}
            disabled={isLoading}
            sx={{
              py: 1.5,
              // background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              background: BUTTON_BG_COLOR,
              textTransform: 'none',
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              submitButtonText || 'continue'
            )}
          </Button>

          {footerText && (
            <Typography fontSize={'11px'} sx={{ mt: 1, textAlign: 'center' }}>
              {footerText}
            </Typography>
          )}
        </Card>
      </Box>
    </Box>
  );
};

export default OnboardingForm;
